import { inject, Injectable, OnDestroy } from '@angular/core'
import { WidgetInterface } from '../interfaces/widget.interface'
import { GridsterItemConfig } from 'angular-gridster2'
import { WidgetNameEnum } from '@modules/widgets-module/enums/widget-name.enum'
import { UtilsService } from '@shared/services/utils.service'
import { DashboardInterface } from '@modules/widgets-module/interfaces/dashboard.interface'
import { WidgetWebSettingsInterface } from '@modules/widgets-module/interfaces/widget-web-settings.interface'
import { SourceWidgets } from '@modules/widgets-module/constants/source-widgets'
import { BehaviorSubject, Subject, take, takeUntil, timer } from 'rxjs'
import { DashboardWebSettingsInterface } from '@modules/widgets-module/interfaces/dashboard-web-settings.interface'
import { DefaultTicker } from '@modules/stocks-module/general/constants/default-ticker'
import { WidgetSizesInterface } from '@modules/widgets-module/interfaces/widget-sizes.interface'
import { FocusedWidgetInterface } from '@modules/widgets-module/interfaces/focused-widget.interface'

@Injectable({
	providedIn: 'root',
})
export class WidgetsService implements OnDestroy {
	protected readonly sourceWidgets: WidgetInterface[] = SourceWidgets
	protected readonly boolean: BooleanConstructor = Boolean
	protected readonly date: DateConstructor = Date
	protected readonly math: Math = Math
	protected readonly defaultTicker: string = DefaultTicker

	public focusedWidget$: BehaviorSubject<FocusedWidgetInterface | null> =
		new BehaviorSubject<FocusedWidgetInterface | null>(null)

	private readonly _utilsService: UtilsService = inject<UtilsService>(UtilsService)

	private _dashboards$: BehaviorSubject<DashboardInterface[]> = new BehaviorSubject<DashboardInterface[]>([])

	private _destroyFocesedWidget: Subject<void> = new Subject<void>()

	get dashboards$(): BehaviorSubject<DashboardInterface[]> {
		return this._dashboards$
	}

	get dashboardsValue(): DashboardInterface[] {
		return this._dashboards$.getValue()
	}

	get activeDashboard(): DashboardInterface | undefined {
		return this.dashboardsValue.find((dashboard: DashboardInterface): boolean => dashboard.isActive)
	}

	// widgets methods -->

	private _restoreGridsterItemStyles(element: HTMLElement): void {
		const focusedWidget: FocusedWidgetInterface | null = this.focusedWidget$.getValue()
		if (!focusedWidget) return

		element.style.width = focusedWidget.originalWidth
		element.style.height = focusedWidget.originalHeight
		element.style.transform = focusedWidget.originalTransform
		element.style.removeProperty('transition')
		element.style.removeProperty('z-index')
	}

	public onFocusWidget(id: string): void {
		const currentFocusWidget: FocusedWidgetInterface | null = this.focusedWidget$.getValue()
		if (currentFocusWidget?.id === id) {
			this.onUnfocusWidget()
			return
		}

		if (currentFocusWidget?.element) this._restoreGridsterItemStyles(currentFocusWidget.element)

		const gridsterEl: HTMLElement | null = document.querySelector<HTMLElement>('#gridster')
		if (!gridsterEl) return
		const gridsterItemEl: HTMLElement | null = gridsterEl.querySelector<HTMLElement>(`gridster-item[id="${id}"]`)
		if (!gridsterItemEl) return

		const widget: WidgetInterface | undefined = this.activeDashboard?.widgets.find(
			(w: WidgetInterface): boolean => w.id === id,
		)
		const sizes: WidgetSizesInterface = widget?.sizes ?? { width: 0, height: 0 }

		this.focusedWidget$.next({
			id,
			element: gridsterItemEl,
			originalTransform: gridsterItemEl.style.transform,
			originalWidth: gridsterItemEl.style.width,
			originalHeight: gridsterItemEl.style.height,
		})

		requestAnimationFrame((): void => {
			if (this.focusedWidget$.getValue()?.id !== id) return
			const gridsterWidth: number = gridsterEl.clientWidth
			const gridsterHeight: number = gridsterEl.clientHeight
			const width: number = this.math.min(sizes.width, gridsterWidth)
			const height: number = this.math.min(sizes.height, gridsterHeight)
			const centerLeft: number = (gridsterWidth - width) / 2
			const centerTop: number = (gridsterHeight - height) / 2

			gridsterItemEl.style.setProperty(
				'transition',
				'transform ease-in-out 0.3s, width ease-in-out 0.3s, height ease-in-out 0.3s',
				'important',
			) // $transition-default
			gridsterItemEl.style.setProperty('z-index', '50', 'important') // $z-index-5
			gridsterItemEl.style.setProperty('width', `${width}px`, 'important')
			gridsterItemEl.style.setProperty('height', `${height}px`, 'important')
			gridsterItemEl.style.setProperty('transform', `translate(${centerLeft}px, ${centerTop}px)`, 'important')
		})
	}

	public onUnfocusWidget(): void {
		const focusedWidget: FocusedWidgetInterface | null = this.focusedWidget$.getValue()
		const focusedItemEl: HTMLElement | null | undefined = focusedWidget?.element
		if (!focusedWidget || !focusedItemEl) return

		this._destroyFocesedWidget.next()

		focusedItemEl.style.width = focusedWidget.originalWidth
		focusedItemEl.style.height = focusedWidget.originalHeight
		focusedItemEl.style.transform = focusedWidget.originalTransform

		this.focusedWidget$.next(null)

		timer(400)
			.pipe(take(1), takeUntil(this._destroyFocesedWidget))
			.subscribe((): void => {
				focusedItemEl.style.removeProperty('transition')
				focusedItemEl.style.removeProperty('z-index')
			})
	}

	public setWidgets(widgets: WidgetInterface[]): void {
		if (!this.activeDashboard) return

		const resDashboard: DashboardInterface = {
			...this.activeDashboard,
			widgets: this.getMergedWidgets(widgets),
		}

		this.updateDashboard(resDashboard)
	}

	public createWidget(widget: WidgetInterface, sizes: GridsterItemConfig | null): void {
		if (!this.activeDashboard) return

		const newWidget: WidgetInterface = {
			...widget,
			id: this._utilsService.generateUniqId(),
			gridsterItem: { ...(sizes || widget.gridsterItem) },
			settings: { ...widget.settings },
		}
		const resDashboard: DashboardInterface = {
			...this.activeDashboard,
			widgets: [...this.activeDashboard.widgets, newWidget],
		}

		this.updateDashboard(resDashboard)
	}

	public updateWidget(widget: WidgetInterface): void {
		if (!this.activeDashboard) return

		const resDashboard: DashboardInterface = {
			...this.activeDashboard,
			widgets: this.activeDashboard.widgets.map(
				(w: WidgetInterface): WidgetInterface => (w.id === widget.id ? widget : w),
			),
		}

		this.updateDashboard(resDashboard)
	}

	public deleteWidget(widget: WidgetInterface): void {
		if (!this.activeDashboard) return

		const resDashboard: DashboardInterface = {
			...this.activeDashboard,
			widgets: this.activeDashboard.widgets.filter((w: WidgetInterface): boolean => w.id !== widget.id),
		}
		this.updateDashboard(resDashboard)
	}

	public getMergedWidgets(widgets: WidgetInterface[]): WidgetInterface[] {
		return widgets.map((widget: WidgetInterface): WidgetInterface => {
			return {
				...this.getSourceWidget(widget.componentName),
				...JSON.parse(JSON.stringify(widget)),
			}
		})
	}

	public getSourceWidget(widgetName: WidgetNameEnum): WidgetInterface | undefined {
		return this.sourceWidgets.find((widget: WidgetInterface): boolean => widget.componentName === widgetName)
	}

	public hasWidgetsNeedTicker(): boolean {
		if (!this.activeDashboard) return false
		return !!this.activeDashboard.widgets.find((widget: WidgetInterface): boolean => widget.needTicker)
	}

	public checkHasWidgetByName(widgetName: WidgetNameEnum): boolean {
		if (!this.activeDashboard) return false

		return !!this.activeDashboard.widgets.find(
			(widget: WidgetInterface): boolean => widget.componentName === widgetName,
		)
	}

	public getWidgetByName(widgetName: WidgetNameEnum): WidgetInterface | undefined {
		if (!this.activeDashboard) return undefined

		return this.activeDashboard.widgets.find(
			(widget: WidgetInterface): boolean => widget.componentName === widgetName,
		)
	}

	public formatWidgetsForWebSettings(widgets: WidgetInterface[]): WidgetWebSettingsInterface[] {
		return widgets.map((widget: WidgetInterface): WidgetWebSettingsInterface => {
			const { x, y, cols, rows }: GridsterItemConfig = widget.gridsterItem

			return {
				id: widget.id || this._utilsService.generateUniqId(),
				componentName: widget.componentName,
				x,
				y,
				cols,
				rows,
				settings: widget.settings,
				...(widget.assetClass && { assetClass: widget.assetClass }),
			}
		})
	}

	public formatWidgetsFromWebSettings(widgets: WidgetWebSettingsInterface[]): WidgetInterface[] {
		const collectedWidgets: (WidgetInterface | null)[] = widgets.map(
			(widgetWS: WidgetWebSettingsInterface): WidgetInterface | null => {
				const sourceWidget: WidgetInterface | undefined = this.sourceWidgets.find(
					(sourceWidget: WidgetInterface): boolean => sourceWidget.componentName === widgetWS.componentName,
				)

				if (!sourceWidget) return null

				return {
					...sourceWidget,
					id: widgetWS.id,
					componentName: widgetWS.componentName,
					gridsterItem: {
						...sourceWidget.gridsterItem,
						x: widgetWS.x,
						y: widgetWS.y,
						cols: widgetWS.cols,
						rows: widgetWS.rows,
						minItemCols: this.math.min(sourceWidget.gridsterItem.minItemCols ?? 1, widgetWS.cols),
						minItemRows: this.math.min(sourceWidget.gridsterItem.minItemRows ?? 1, widgetWS.rows),
					},
					settings: widgetWS.settings,
					...(widgetWS.assetClass && { assetClass: widgetWS.assetClass }),
				}
			},
		)

		return collectedWidgets.filter(this.boolean) as WidgetInterface[]
	}

	// <-- widgets methods

	// dashboards methods -->

	public setDashboards(dashboards: DashboardInterface[]): void {
		this._dashboards$.next(dashboards)
	}

	public createDashboard(title: string): void {
		const dashboards: DashboardInterface[] = this.dashboardsValue.map(
			(dashboard: DashboardInterface): DashboardInterface => {
				return {
					...dashboard,
					isActive: false,
				}
			},
		)

		const id: string = this._utilsService.generateUniqId()

		const newDashboard: DashboardInterface = {
			id,
			title,
			isActive: true,
			liveTicker: this.defaultTicker,
			widgets: [],
		}

		this.setDashboards([...dashboards, newDashboard])
	}

	public setLiveTickerToDashboard(ticker: string): void {
		if (!this.activeDashboard) return
		if (this.activeDashboard.liveTicker === ticker) return
		const resDashboard: DashboardInterface = {
			...this.activeDashboard,
			liveTicker: ticker,
		}
		this.updateDashboard(resDashboard)
	}

	public updateDashboard(dashboard: DashboardInterface): void {
		const updatedDashboards: DashboardInterface[] = this.dashboardsValue.map(
			(db: DashboardInterface): DashboardInterface => (db.id === dashboard.id ? dashboard : db),
		)
		this.setDashboards(updatedDashboards)
	}

	public deleteDashboard(dashboardId: string): void {
		if (this.activeDashboard?.id === dashboardId) {
			const firstDashboard: DashboardInterface | undefined = this.dashboardsValue[0]
			const secondDashboard: DashboardInterface | undefined = this.dashboardsValue[1]
			const resDashboard: DashboardInterface | undefined = !firstDashboard?.isActive
				? firstDashboard
				: secondDashboard
			if (!resDashboard) return
			this.setActiveDashboard(resDashboard)
		}

		const resDashboards: DashboardInterface[] = this.dashboardsValue.filter(
			(dashboard: DashboardInterface): boolean => dashboard.id !== dashboardId,
		)
		this.setDashboards(resDashboards)
	}

	public renameDashboard(dashboard: DashboardInterface, newTitle: string): void {
		const resDashboard: DashboardInterface = {
			...dashboard,
			title: newTitle.trim(),
		}

		this.updateDashboard(resDashboard)
	}

	public initDefaultDashboard(title: string): void {
		if (this.dashboardsValue.length) return
		this.createDashboard(title)
	}

	public setActiveDashboard(dashboard: DashboardInterface): void {
		if (dashboard.isActive) return
		const { id }: DashboardInterface = dashboard

		const updatedDashboards: DashboardInterface[] = this.dashboardsValue.map(
			(dashboard: DashboardInterface): DashboardInterface => {
				const isActive: boolean = dashboard.id === id
				return { ...dashboard, isActive }
			},
		)
		this.setDashboards(updatedDashboards)
	}

	public formatDashboardsForWebSettings(): DashboardWebSettingsInterface[] {
		return this.dashboardsValue.map((dashboard: DashboardInterface): DashboardWebSettingsInterface => {
			return {
				...dashboard,
				widgets: this.formatWidgetsForWebSettings(dashboard.widgets),
			}
		})
	}

	public applyDashboardsFromWebSettings(dashboards: DashboardWebSettingsInterface[]): void {
		const resDashboards: DashboardInterface[] = dashboards.map(
			(dashboard: DashboardWebSettingsInterface): DashboardInterface => {
				return {
					...dashboard,
					widgets: this.formatWidgetsFromWebSettings(dashboard.widgets),
				}
			},
		)
		this.setDashboards(resDashboards)
	}

	// <-- dashboards methods

	ngOnDestroy(): void {
		this._destroyFocesedWidget.next()
		this._destroyFocesedWidget.complete()
	}
}
